import { useEffect, useState } from 'react'
import { listInvoiceTemplates, type InvoiceTemplateInfo } from '../api'
import { useTabs } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import DataTable, { type Column } from '../components/DataTable'
import { templateVersionNumber } from '../lookups'
import { fill, PAGES_INVOICE_TEMPLATES_LIST_TEXTS as T } from '../../shared/texts'

const EditIcon = () => <img src="/icons/edit.svg" alt="" aria-hidden="true" />

// List of PDF templates (like Kunden/Abos). Double-click / edit opens the template in its own
// editor tab. Two kinds live here since the certificates arrived — the "Art" column keeps them
// apart, because the placeholders a template may use depend on it.
//
// There is deliberately no "new template" button: which template a document uses is decided in
// code — TEMPLATE_BRAND (services/pdf.ts) for invoices, certificateTemplateName() for
// certificates. A template created here would show up in this list and open in the editor, but
// no document would ever use it; assigning one needs a code change anyway.
const INFO = T.info

// `embedded`: shown under System → Vorlagen → PDF, below the page's own heading — so no second big
// title, the explanation as a plain line instead.
export default function InvoiceTemplatesList({ embedded = false }: { embedded?: boolean } = {}) {
    const { toast } = useToast()
    const { openTab } = useTabs()

    const [rows, setRows] = useState<InvoiceTemplateInfo[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    // Messages also as a toast — the inline notice can sit far below the fold.
    const failMsg = (msg: string) => { setError(msg); toast(msg, 'err') }

    const load = () => {
        setLoading(true)
        listInvoiceTemplates()
            .then((t) => { setRows(t); setError('') })
            .catch((e) => failMsg(e?.message ?? T.loadFailed))
            .finally(() => setLoading(false))
    }
    useEffect(load, [])

    const openEdit = (t: InvoiceTemplateInfo) => openTab({
        id: `invoice-template-${t.brand}-${t.version}`,
        name: fill(T.tabName, { label: t.label }),
        component: 'invoice-template-form',
        config: { brand: t.brand, version: t.version },
    })

    const columns: Column<InvoiceTemplateInfo>[] = [
        { key: 'label', header: T.colTemplate, defaultWidth: 260, render: (t) => t.label },
        {
            key: 'kind', header: T.colKind, defaultWidth: 130,
            render: (t) => (t.kind === 'certificate' ? T.kindCertificate : T.kindInvoice),
        },
        {
            // The active version is the one new documents use; the others stay for re-rendering old ones (137b).
            key: 'version', header: T.colVersion, defaultWidth: 140,
            render: (t) => (
                <>
                    {templateVersionNumber(t.version)}
                    {t.active && <> <span className="badge sent" title={T.activeHint}>{T.active}</span></>}
                </>
            ),
        },
        {
            key: 'actions', header: T.colActions, defaultWidth: 90, sticky: true,
            render: (t) => (
                <div className="row-actions">
                    <button className="icon-action" title={T.edit} aria-label={T.edit}
                        onClick={() => openEdit(t)}><EditIcon /></button>
                </div>
            ),
        },
    ]

    return (
        <div className="list-page">
            {embedded
                ? <p className="muted" style={{ marginTop: 0 }}>{INFO}</p>
                : <ListToolbar title={T.title} infoText={INFO} />}

            {error && <div className="notice err">{error}</div>}

            <ListPanel count={rows.length} grandTotal={rows.length} filtered={false}>
                {loading ? <p className="muted">{T.loading}</p> : rows.length === 0 ? (
                    <p className="muted">{T.empty}</p>
                ) : (
                    <DataTable
                        tableId="invoice-templates"
                        columns={columns}
                        rows={rows}
                        rowKey={(t) => `${t.brand}/${t.version}`}
                        onRowActivate={openEdit}
                    />
                )}
            </ListPanel>

        </div>
    )
}
